import { Link } from 'react-router-dom';
import { Phone, Mail, MapPin, Clock, Facebook, Linkedin, Instagram } from 'lucide-react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_a2fe651e-5a75-4c03-b574-7770491eb762/artifacts/uqksscxp_7cfcc9_f4e11a8d48f74ecc9017bb515bda0ff5~mv2.png.avif";

const quickLinks = [
  { name: 'Home', href: '/' },
  { name: 'About Us', href: '/about' },
  { name: 'Our Services', href: '/services' },
  { name: 'Our Team', href: '/team' },
  { name: 'Testimonials', href: '/testimonials' },
  { name: 'Contact Us', href: '/contact' },
];

const services = [
  'Skilled Nursing',
  'Physical Therapy',
  'Occupational Therapy',
  'Speech Therapy',
  'Wound Care',
  'Cardiac & Pulmonary Care',
];

export const Footer = () => {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="bg-[#0F172A] text-white" data-testid="footer">
      {/* Main Footer */}
      <div className="container mx-auto px-4 md:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Company Info */}
          <div className="space-y-4">
            <div className="bg-white rounded-lg p-3 inline-block">
              <img
                src={LOGO_URL}
                alt="Senior Care Solutions"
                className="h-10 w-auto"
              />
            </div>
            <p className="text-slate-300 leading-relaxed">
              Providing compassionate, skilled home healthcare services to seniors throughout Florida. 
              Medicare certified & CHAP/ACHC accredited.
            </p>
            <div className="flex gap-4">
              <a 
                href="#" 
                className="w-10 h-10 rounded-full bg-[#000080] flex items-center justify-center hover:bg-[#00A878] transition-colors"
                aria-label="Facebook"
                data-testid="social-facebook"
              >
                <Facebook className="w-5 h-5" />
              </a>
              <a 
                href="#" 
                className="w-10 h-10 rounded-full bg-[#000080] flex items-center justify-center hover:bg-[#00A878] transition-colors"
                aria-label="LinkedIn"
                data-testid="social-linkedin"
              >
                <Linkedin className="w-5 h-5" />
              </a>
              <a 
                href="#" 
                className="w-10 h-10 rounded-full bg-[#000080] flex items-center justify-center hover:bg-[#00A878] transition-colors"
                aria-label="Instagram"
                data-testid="social-instagram"
              >
                <Instagram className="w-5 h-5" />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-xl font-semibold mb-6 text-white">Quick Links</h3>
            <ul className="space-y-3">
              {quickLinks.map((link) => (
                <li key={link.name}>
                  <Link
                    to={link.href}
                    className="text-slate-300 hover:text-[#00A878] transition-colors"
                    data-testid={`footer-link-${link.name.toLowerCase().replace(/\s/g, '-')}`}
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Services */}
          <div>
            <h3 className="text-xl font-semibold mb-6 text-white">Our Services</h3>
            <ul className="space-y-3">
              {services.map((service) => (
                <li key={service}>
                  <Link
                    to="/services"
                    className="text-slate-300 hover:text-[#00A878] transition-colors"
                  >
                    {service}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h3 className="text-xl font-semibold mb-6 text-white">Contact Us</h3>
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <MapPin className="w-5 h-5 text-[#00A878] flex-shrink-0 mt-1" />
                <span className="text-slate-300">
                  7000 W. Palmetto Park Road<br />
                  Ste. 210<br />
                  Boca Raton, FL 33433
                </span>
              </li>
              <li>
                <a 
                  href="tel:+18773106120" 
                  className="flex items-center gap-3 text-slate-300 hover:text-[#00A878] transition-colors"
                  data-testid="footer-phone"
                >
                  <Phone className="w-5 h-5 text-[#00A878]" />
                  877.310.6120
                </a>
              </li>
              <li>
                <a 
                  href="mailto:info@seniorcaresolutionsfl.com" 
                  className="flex items-center gap-3 text-slate-300 hover:text-[#00A878] transition-colors"
                  data-testid="footer-email"
                >
                  <Mail className="w-5 h-5 text-[#00A878]" />
                  info@seniorcaresolutionsfl.com
                </a>
              </li>
              <li className="flex items-start gap-3">
                <Clock className="w-5 h-5 text-[#00A878] flex-shrink-0 mt-1" />
                <span className="text-slate-300">
                  24/7 Clinical Support<br />
                  Office: Mon-Fri 8AM-5PM
                </span>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-slate-700">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 py-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-slate-400 text-sm text-center md:text-left">
              © {currentYear} Senior Care Solutions. All rights reserved.
            </p>
            <div className="flex flex-wrap justify-center gap-6 text-sm">
              <a href="#" className="text-slate-400 hover:text-[#00A878] transition-colors">
                Privacy Policy
              </a>
              <a href="#" className="text-slate-400 hover:text-[#00A878] transition-colors">
                Terms of Service
              </a>
              <a href="#" className="text-slate-400 hover:text-[#00A878] transition-colors">
                HIPAA Notice
              </a>
              <a 
                href="https://bp.agency" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-slate-400 hover:text-[#00A878] transition-colors"
              >
                Website Designed By Brand Partners
              </a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
